/**
 * What: this is a component that tracks the content view
 * Why: easier to implement as a component and provides greater control over how we track content views
 * How: in the hook we get the current page context, tracking client, pass down the params to the handler -> done
 */
import {
  TrackingPageContext,
  trackContentView,
} from '@aboutyou/legacy-tracking-sdk';
import {
  useCanGoBack,
  useHistory,
  useInitialLocation,
  useIsLastNavigationHistoryBack,
} from '@aboutyou/router';
import {usePrevious} from '@aboutyou/utils';
import React, {FC, useEffect} from 'react';
import {useTrackingPageContext} from '../context/PageTrackingContext';
import {trackCustomerDataOnce} from '../utils/trackCustomerDataOnce';
import {useTracking} from '../utils/utils';
import {useConvertUserAndSessionToCustomer} from './TrackCustomer/useConvertUserAndSessionToCustomer';
// eslint-disable-next-line monorepo/no-internal-import
import {AuthSearchParams} from '@aboutyou/logic-components/src/features/Authentication/hooks/constants';
// eslint-disable-next-line monorepo/no-internal-import
import {useOffcanvasOpenState} from '@aboutyou/logic-components/src/backend-services/Navigation/useOffcanvasOpenState';
// eslint-disable-next-line monorepo/no-internal-import
import {useWalletStateManager} from '@aboutyou/logic-components/src/backend-services/Wallet/utils/useWalletStateManager';
import {useForceUpdateOnBasketFlyoutClose} from '../trackingHooks/useForceUpdateOnBasketFlyoutClose';

interface Props {
  breadcrumbsPath?: string;
  pageTitle: string;
  overridePageProps?: {type?: string; id?: number};
}

export const TrackContentPageView: FC<Props> = props =>
  process.browser ? <TrackContentPageViewClient {...props} /> : <></>;

export const TrackContentPageViewClient: FC<Props> = props => {
  useTrackContentView(props);
  return null;
};

function useTrackContentView({
  breadcrumbsPath,
  pageTitle,
  overridePageProps,
}: Props) {
  const pageContext = useTrackingPageContext();
  const {TrackingClient} = useTracking();
  const customer = useConvertUserAndSessionToCustomer();
  const isContentViewDisabled = useIsContentViewDisabled();

  // This hook returns a new value whenever the basket flyout is closed,
  // which is used to force an update in the useEffect below, which in turn fires the content_view event for the page beneath the basket flyout.
  const forceUpdateOnBasketFlyoutClose = useForceUpdateOnBasketFlyoutClose();

  // eslint-disable-next-line local-rules/too-complex-useeffects -- TODO: Cleanup complex useEffect https://aboutyou.atlassian.net/browse/ASFE-7605
  useEffect(() => {
    if (pageContext !== null && !isContentViewDisabled) {
      const {...page} = pageContext;

      if (customer) {
        trackCustomerDataOnce(TrackingClient, customer);

        // content_view for basket flyout triggers before add_to_cart one (for example on the ADP), therefore a setTimeout is needed here
        const timer = setTimeout(() => {
          TrackingClient.track(
            trackContentView({
              page: {
                ...page,
                ...overridePageProps,
                title: pageTitle,
              } as unknown as TrackingPageContext,
            }),
          );
        });

        return () => {
          clearTimeout(timer);
        };
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isContentViewDisabled,
    pageContext.id,
    pageContext.path,
    pageContext.search,
    pageContext.type,
    TrackingClient,
    breadcrumbsPath,
    pageTitle,
    customer?.type,
    forceUpdateOnBasketFlyoutClose,
  ]);
}

// Prevents firing the content_view event in specific cases,
// such as when search parameters are updated after users apply stream filters,
// which doesn't constitute a new content view.
const useIsContentViewDisabled = () => {
  const history = useHistory();
  const initialLocation = useInitialLocation();
  const canGoBack = useCanGoBack();
  const isLastNavigationHistoryBack = useIsLastNavigationHistoryBack();
  const {isOpen: isOffcanvasOpen} = useOffcanvasOpenState();
  const historyState = history.location?.state as Record<string, boolean>;
  const {isOpen: isWalletOpen} = useWalletStateManager();

  const hasFilterOrSortParams = Boolean(
    historyState?.isFiltering || historyState?.isSorting,
  );
  const prevHasFilterOrSortParams = usePrevious(hasFilterOrSortParams);

  if (history.location?.search.includes(AuthSearchParams.LoginFlow)) {
    return true;
  }

  // If the wallet is open, prevent the page content_view from firing, since the wallet's content_view already fires on tab switch.
  if (isWalletOpen) {
    return true;
  }
  if (isOffcanvasOpen) {
    return true;
  }

  // On direct entry we always want to fire the content view event
  if (!canGoBack) {
    return false;
  }

  // Otherwise, if filter or sort params are present we want to prevent content_view from being fired
  if (hasFilterOrSortParams && !isLastNavigationHistoryBack) {
    return true;
  }

  // If user resets the filter or sort params, we want to prevent content_view from being fired
  if (
    prevHasFilterOrSortParams &&
    initialLocation.pathname === history.location.pathname
  ) {
    return true;
  }

  return false;
};
